import { requestV3 } from 'Features/Auth/axios-client'
import {
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from '@tanstack/react-query'
import {
  BaseSourceSiteWorkCentre,
  RUN_METHODS,
  STATUS_MAP,
} from 'Features/Projects/Scenarios/Templates/PlanTemplateSummary.types'

// A template run can take minutes. While any work centre has a method still
// going, re-read the statuses so the page shows each result when it lands (it
// used to need a manual Refresh). Polling stops once none are running.
const RUNNING_STATUSES = new Set(['initiated', 'processing', 'running'])
const STATUS_POLL_MS = 10 * 1000

export const isRunning = (status?: string | null) =>
  RUNNING_STATUSES.has(String(status))

export const hasRunningTemplate = (rows: BaseSourceSiteWorkCentre[] = []) =>
  rows.some((row) =>
    RUN_METHODS.some((method) => isRunning(row[STATUS_MAP[method]] as string))
  )

const fetchBaseSourceSiteWorkCentre = async (
  projectId: string,
  scenarioId: string
) => {
  const { data } = await requestV3({
    url: `base-source-site-workcentre?projectId=${projectId}&scenarioId=${scenarioId}`,
  })
  return data
}

export const useBaseSourceSiteWorkCentre = ({
  projectId,
  scenarioId,
}: {
  projectId: string
  scenarioId: string
}): UseQueryResult<BaseSourceSiteWorkCentre[]> => {
  return useQuery({
    queryKey: ['base-source-site-workcentre', projectId, scenarioId],
    queryFn: () => fetchBaseSourceSiteWorkCentre(projectId, scenarioId),
    select: (data) => data.data,
    gcTime: Infinity,
    refetchInterval: (query) =>
      hasRunningTemplate(
        (query.state.data as { data?: BaseSourceSiteWorkCentre[] } | undefined)
          ?.data
      )
        ? STATUS_POLL_MS
        : false,
  })
}

const updateTemplateGeneration = async ({
  projectId,
  scenarioId,
  state,
}: {
  projectId: string
  scenarioId: string
  state: BaseSourceSiteWorkCentre[]
}) => {
  const { data } = await requestV3({
    url: `/updateTemplateGeneration`,
    method: 'POST',
    data: {
      projectId,
      scenarioId,
      state,
    },
  })
  return data
}

export const useUpdateTemplateGeneration = (
  onSuccess: () => void,
  onError: () => void
) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: updateTemplateGeneration,
    // Re-read the rows so the saved state (including an 'initiated' status) is
    // what the query holds, which is what starts the status polling. The
    // preferred method also decides which template the method comparison
    // marks and which cycle times replenishment uses.
    onSuccess: async (_, { projectId, scenarioId }) => {
      await Promise.all(
        [
          'base-source-site-workcentre',
          'template-method-comparison',
          'replenishment-cycle-time',
        ].map((key) =>
          queryClient.invalidateQueries({
            queryKey: [key, projectId, scenarioId],
          })
        )
      )
      onSuccess()
    },
    onError,
  })
}

// Each work centre is solved with every one of RUN_METHODS.
export type GenerateParams = {
  projectId: string
  scenarioId: string
  workcentreIds: number[]
}

const generateTemplates = async (generateParams: GenerateParams) => {
  const { data } = await requestV3({
    url: `/generate-templates`,
    method: 'PUT',
    data: generateParams,
  })
  return data
}

// The API sets the statuses to 'initiated' once the run exists, so re-read
// them either way: that starts the polling, or undoes the optimistic state.
export const useGenerateTemplates = (
  onSuccess: () => void,
  onError: () => void
) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: generateTemplates,
    onSuccess,
    onError,
    onSettled: () =>
      queryClient.invalidateQueries({
        queryKey: ['base-source-site-workcentre'],
      }),
  })
}
